import {
  ReporterAvailability,
  DayAvailability,
  SetAvailabilityRequest,
  ApiResponse
} from '../types/availability';
import { AuthenticationError } from './AuthenticationError';
import { devLog } from './devTools';

const API_BASE_URL = window.APP_CONFIG?.apiUrl || window.location.origin;

// Handle API errors
async function handleApiResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // Check for authentication error (401)
    if (response.status === 401) {
      throw new AuthenticationError('Your session has expired. Please log in again.');
    }
    
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `API Error: ${response.status}`);
  }
  
  return response.json();
}

export const availabilityApi = {
  
  /**
   * Get complete availability data for the authenticated reporter
   */
  async getAvailability(): Promise<ReporterAvailability> {
    const response = await fetch(`${API_BASE_URL}/api/availability`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include' // Use cookie-based authentication
    });
    
    return handleApiResponse<ReporterAvailability>(response);
  },

  /**
   * Set availability for a specific date
   */
  async setAvailability(request: SetAvailabilityRequest): Promise<ApiResponse> {
    devLog('🌐 availabilityApi.setAvailability called');
    devLog('🔗 API_BASE_URL:', API_BASE_URL);
    devLog('📤 Request payload:', JSON.stringify(request, null, 2));
    
    const response = await fetch(`${API_BASE_URL}/api/availability`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(request)
    });
    
    devLog('📡 Response status:', response.status, response.statusText);
    devLog('📡 Response headers:', Object.fromEntries(response.headers.entries()));
    
    const result = await handleApiResponse<ApiResponse>(response);
    devLog('✅ API response result:', result);
    return result;
  },

  /**
   * Remove availability for a date
   */
  async removeAvailability(availabilityDate: string): Promise<ApiResponse> {
    const params = new URLSearchParams({ availabilityDate });
    
    const response = await fetch(`${API_BASE_URL}/api/availability?${params.toString()}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
      },
      credentials: 'include'
    });
    
    return handleApiResponse<ApiResponse>(response);
  },

  /**
   * Get availability for a specific date
   */
  async getDateAvailability(date: string): Promise<DayAvailability> {
    const response = await fetch(`${API_BASE_URL}/api/availability/date/${date}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include'
    });
    
    return handleApiResponse<DayAvailability>(response);
  },

  /**
   * Copy availability from previous week to current week
   */
  async copyLastWeek(currentWeekStart: string): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/api/availability/copy-last-week`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ currentWeekStart })
    });
    
    return handleApiResponse<ApiResponse>(response);
  },

  /**
   * Check if a schedule for a specific date can be edited
   */
  async checkEditable(date: string): Promise<{
    canEdit: boolean;
    deadline: string;
    deadlineTimezone: string;
    deadlineFormatted: string;
    reason?: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/availability/check-editable/${date}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include'
    });
    
    return handleApiResponse(response);
  }
};

export default availabilityApi;